import { IAssignableEventModel } from "../models/assignableEventModel";

/** One row of a planner event picker (instructor transfer, bulk delete). */
export interface IEventPickerOption {
  id: number;
  /** Full single-line label. Drives the selected chip and the panel's text filter. */
  name: string;
  /** First row line: "15.09.2026 (вт) 02:00". */
  primary: string;
  /** Second row line: "spinning • Petko Petkov". */
  secondary: string;
}

const NO_INSTRUCTOR_LABEL = "без инструктор";

/**
 * Short Bulgarian weekday names indexed by `Date.getDay()`. Hardcoded rather than taken from
 * the date-fns locale so the rendered label stays stable and assertable.
 */
const WEEKDAY_LABELS = ["нд", "пн", "вт", "ср", "чт", "пт", "сб"];

/**
 * Parses the studio-local wall clock the API returns ("2026-09-19T18:00:00", no timezone
 * suffix) into a Date whose local fields carry those exact digits back out again.
 */
const parseStudioLocalDate = (value: string): Date | null => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const pad = (value: number): string => String(value).padStart(2, "0");

/**
 * Splits an event into the two lines of a picker row: when + what.
 *
 * The row is rendered inside a virtualised list, where every row is pinned to a fixed pixel
 * height, so the text has to be split up front instead of being left to wrap.
 *
 * `extraDetails` are appended to the second line (e.g. "3 резервации").
 */
export const buildEventPickerOption = (
  event: IAssignableEventModel,
  extraDetails: string[] = [],
): IEventPickerOption => {
  const date = parseStudioLocalDate(event.date);
  const instructor = event.currentInstructor?.trim()
    ? event.currentInstructor
    : NO_INSTRUCTOR_LABEL;

  const primary = date
    ? `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} (${
        WEEKDAY_LABELS[date.getDay()]
      }) ${pad(date.getHours())}:${pad(date.getMinutes())}`
    : "";
  const secondary = [event.name, instructor, ...extraDetails].join(" • ");

  return {
    id: event.id,
    name: primary ? `${primary} • ${secondary}` : secondary,
    primary,
    secondary,
  };
};
